import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import {capitalize} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	isWebview,
	arrAvg,
	repsMaxHeightDifference,
	centerPosition,
	projectKeypointsUntoAxis,
	roundDecimal,
	singleLegKneeScore,
	calculateThighLength,
	calculateCalfLength,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	nearest,
	countPerRepHandsOffHips,
	singleLegSquatBalanceScore
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setPoses,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Leaderboard from './Leaderboard'
import Indicators from './Indicators'
import Poses from './Poses'
import useMetronome from '../../hooks/useMetronome'
import calfRaiseTestData from './calfRaiseTestData.json'
import startSound from '../../assets/start.mp3'

const SCORE_THRESHOLD = 0.3
//this is how much movement must occur before we count a rep 
//as being started. Used to prevent false starts due to noise
//in pose predictions
const START_TOLERANCE = 0.015
const ESTIMATED_HEIGHT_INCHES = 69
const INPUT_SIZE = 512

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
	multiPoseMaxDimension:INPUT_SIZE
}

const KeyRepMetricsTable = ({leftRepHeights=[],rightRepHeights=[]})=>{
	return (
		<Table striped bordered>
			<tr className="bg-dark text-light">
				<th/>
				<th>Left Rep Count</th>
				<th>Right Rep Count</th>
			</tr>
			<tr>
				<td/>
				<td>{leftRepHeights.length}</td>
				<td>{rightRepHeights.length}</td>
			</tr>
			<tr className="bg-dark text-light">
				<th>Rep</th>
				<th>Left Height (inches)</th>
				<th>Right Height (inches)</th>
			</tr>
			{[...Array(Math.max(leftRepHeights.length,rightRepHeights.length))].map((_,i)=>(
				<tr>
					<td>{i+1}</td>
					<td>{i<leftRepHeights.length?`${leftRepHeights[i].toFixed(1)}"`:'-'}</td>
					<td>{i<rightRepHeights.length?`${rightRepHeights[i].toFixed(1)}"`:'-'}</td>
				</tr>
			))}
			<tr className="bg-dark text-light">
				<th></th>
				<th>Left Height Summary (inches)</th>
				<th>Right Height Summary (inches)</th>
			</tr>
			<tr>
				<td>Min</td>
				<td>{Math.min(...leftRepHeights).toFixed(1)}"</td>
				<td>{Math.min(...rightRepHeights).toFixed(1)}"</td>
			</tr>
			<tr>
				<td>Average</td>
				<td>{arrAvg(leftRepHeights).toFixed(1)}"</td>
				<td>{arrAvg(rightRepHeights).toFixed(1)}"</td>
			</tr>
			<tr>
				<td>Max</td>
				<td>{Math.max(...leftRepHeights).toFixed(1)}"</td>
				<td>{Math.max(...rightRepHeights).toFixed(1)}"</td>
			</tr>
		</Table>
	)
}

const CalfRaiseDemo = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const countRef = useRef({
		left:0,
		right:0
	})

	const leftCountSpanRef = useRef(null)
	const rightCountSpanRef = useRef(null)

	const [timeLeft,{start:startCoundown,reset:resetCountdown}] = useCountDown(3000, 1000)
	const countingDownRef = useRef(false)

	const {
		startMetronome,
		stopMetronome,
	} = useMetronome(30, 4, [startSound,startSound])

	const initializeGuides = async ()=>{
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,INPUT_SIZE,INPUT_SIZE/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
		if (poses.length === 0) return
		const keypoints = poses[0].keypoints
		const side = balancingSide(keypoints)
		//const nose = keypoints[0]
		//using left hand for now
		const nose = keypoints[0]
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		positionRef.current = 'balancing'
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right

		squatStateSpanRef.current.innerText = stateDisplayTextMap['balancing']
		const height = poseHeight(keypoints)
		const startY = centerPosition(keypoints).y - START_TOLERANCE*height
		guidesRef.current.left = {height,startY}
		guidesRef.current.right = {height,startY}
	}

	//stores results from squat analysis
	const emptyResults = ()=>({
		keypointLog:[],
		left:{
			reps:{
				started:[],
				ended:[]
			},
			centerPosition:[],
		},
		right:{
			reps:{
				started:[],
				ended:[]
			},
			centerPosition:[],
		}
	})

	const [results,setResults] = useState(emptyResults())//useState(calfRaiseTestData)

	const resultsRef = useRef(null)

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef({
		left:{
			height: 224.76441420267096,
			startY: 82.60544982104602
		},
		right:{
			height: 224.76441420267096,
			startY: 82.60544982104602
		}
	})

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect( async ()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		standing:'Standing',
		raising:'Raising',
		waiting:'Waiting',
		balancing:'Balancing',
		setup:'Get Ready',
		initialize:'Position Yourself'
	}

	const initState = 'waiting'
	const positionRef = useRef(initState)
	const squatStateSpanRef = useRef(null)

	const infer = useCallback( async () => {
		//console.log(store.getState())
		//map from current state to trigger for transitioning to next
		//state
		const onTransition = (t)=>{
			if (!squatStateSpanRef.current) return
			squatStateSpanRef.current.innerText = stateDisplayTextMap[positionRef.current]
		}
		
		//runs on every inference to log data continuously (eg nose & knee positions)
		const dataLogger = (keypoints,t)=>{
			const side = balancingSide(keypoints)
			if(side==='none') return 
			const nose = keypoints[0]
			const leftKnee = keypoints[13]
			const rightKnee = keypoints[14]

			const leftAnkle = keypoints[15]
			const rightAnkle = keypoints[16]

			resultsRef.current[side].centerPosition.push({
				...centerPosition(keypoints),
				t
			})
		}

		//These are triggers that get fired if in the current state
		//For example: if the current state is standing, then only the 
		//trigger for standing gets fired 
		const transitionTriggers = {
			initialize:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//left arm keypoints
				const lWrist = keypoints[10]
				const lElbow = keypoints[8]
				const lShoulder = keypoints[6]

				const lArmUp = (
					lWrist.y < lShoulder.y &&
					lElbow.y < lShoulder.y
				)

				//right arm keypoints
				const rWrist = keypoints[9]
				const rElbow = keypoints[7]
				const rShoulder = keypoints[5]

				const rArmUp = (
					rWrist.y < rShoulder.y &&
					rElbow.y < rShoulder.y
				)

				if(lArmUp&&rArmUp){
					positionRef.current = 'setup'
					overlayRef.current.innerText = 'Get Ready'
					setShowOverlay(true)
				}
			},
			setup:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//if wrist is in this radius then hands are considered "on hip"
				const hipRadius = 30
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = poseHeight(keypoints)*0.05

				//left arm keypoints
				const lWrist = keypoints[9]
				const lElbow = keypoints[7]
				const lShoulder = keypoints[5]
				const lHip = keypoints[11]
				const leftAnkle = keypoints[15]
				const leftKnee = keypoints[13]


				//right arm keypoints
				const rWrist = keypoints[10]
				const rElbow = keypoints[8]
				const rShoulder = keypoints[6]
				const rHip = keypoints[12]
				const rightAnkle = keypoints[16]
				const rightKnee = keypoints[14]
				
				//Propositions
				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance

			
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance

				

				if (leftAnkleLifted || rightAnkleLifted) {
					setShowOverlay(true)
					initializeGuides()
					overlayRef.current.innerText = 'GO!'
					setTimeout(()=>{setShowOverlay(false)},1500)
				}
			},
			standing:(keypoints,t)=>{
				const side = balancingSide(keypoints)
				const leftAnkle = keypoints[15]
				const rightAnkle = keypoints[16]
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = poseHeight(keypoints)*0.05
				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance
				
				if(leftAnkleLifted||rightAnkleLifted){
					//reset guides
					const height = poseHeight(keypoints)
					const startY = centerPosition(keypoints).y - START_TOLERANCE*height
					//guidesRef.current[side] = {height,startY}
					positionRef.current = 'balancing'
					console.log(guidesRef.current)
				}
				//if returning to standing position and data exists for both sides
				//then exercise is finished and transition to results
				if ( 
					countRef.current.left > 0 && 
					countRef.current.right > 0 
				) {
					console.log(guidesRef.current)
					console.log(resultsRef.current)
					setShowOverlay(true)
					overlayRef.current.innerText = 'Done. Processing Results'
					setTimeout(()=>{setShowOverlay(false)},2000)
					setTimeout(()=>{
						cancelInferencing()
						setShowGuides(false)
						setResults(resultsRef.current)
						stopMetronome()
						setShowResults(true)
						//testVidRef.current.pause()
						trackResultsRef.current = false
						//testVidRef.current.currentTime = 0
					},2000)
				}
			},
			raising:(keypoints,t)=>{
				console.log('Raising')
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = poseHeight(keypoints)*0.05
				const nose = keypoints[0]
				const leftAnkle = keypoints[15]
				const chest = centerPosition(keypoints)

				const rightAnkle = keypoints[16]

				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance
				const side = balancingSide(keypoints)
				//if in balancing state transition to stand 
				//when one both ankles below threshold
				if ( !leftAnkleLifted && !rightAnkleLifted ) {
					positionRef.current = 'standing'
					positionRef.current = 'balancing'
					
					resultsRef.current[side].reps.ended.push(t)
					if(rightAnkleLifted){
						countRef.current.left += 1
					}else{
						countRef.current.right += 1
					}

					leftCountSpanRef.current.innerText = countRef.current.left
					rightCountSpanRef.current.innerText = countRef.current.right
				}
				
				if ( chest.y >= guidesRef.current[side].startY ) {
					positionRef.current = 'balancing'
					
					resultsRef.current[side].reps.ended.push(t)
					if(rightAnkleLifted){
						countRef.current.left += 1
					}else{
						countRef.current.right += 1
					}

					leftCountSpanRef.current.innerText = countRef.current.left
					rightCountSpanRef.current.innerText = countRef.current.right
				}
			},
			balancing:(keypoints,t)=>{
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = poseHeight(keypoints)*0.05

				const side = balancingSide(keypoints)
				const leftAnkle = keypoints[15]

				const rightAnkle = keypoints[16]

				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance

				//if in balancing state transition to stand 
				//when one both ankles below threshold
				if ( !leftAnkleLifted && !rightAnkleLifted ) {
					positionRef.current = 'standing'
					//resultsRef.current[resultsRef.current.balanceFoot].end = t
				}
				const nose = keypoints[0]
				const chest = centerPosition(keypoints)
				console.log(chest.y,guidesRef.current[side].startY)
				if ( chest.y <= guidesRef.current[side].startY ) {
					const side = balancingSide(keypoints)
					resultsRef.current[side].reps.started.push(t)
					positionRef.current = 'raising'
				}
			},
		}

		if (!webcamRef.current||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(webcamRef.current.video,0,0,INPUT_SIZE,INPUT_SIZE/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				console.log('Not initialized')
				return
			}
			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				posesRef.current = poses
				dispatch(setPoses(poses.map(p=>{
					p.keypoints = p.keypoints.map(k=>({...k,x:INPUT_SIZE-k.x}))
					return p
				})))
				if ( poses.length > 0 ) {
					resultsRef.current.keypointLog.push(poses)
					const keypoints = poses[0].keypoints
					const nose = keypoints[0]
					const t = Date.now()
					if ( nose.score >= SCORE_THRESHOLD ) {
						if ( !['setup','initialize'].includes(positionRef.current) ) dataLogger(keypoints,t) 
						const transitionTrigger = transitionTriggers[positionRef.current]
						if (transitionTrigger) {
							transitionTrigger(keypoints,t)
							onTransition(t)
						}
					}
				}
			} catch(err) {
				console.log(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)

	const startDemo = async ()=>{
		startMetronome()
		setStarted(true)
		cancelInferencing()
		resetCountdown()
		setShowGuides(false)

		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right
		squatStateSpanRef.current.innerText = stateDisplayTextMap['initialize']

		initializedRef.current = true
		positionRef.current = 'initialize'
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		//testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,INPUT_SIZE,INPUT_SIZE/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false)
		setShowGuides(false)
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		stopMetronome()
		countingDownRef.current = false
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
	}

	const stopDemo = ()=>{
		setShowOverlay(false)
		setShowGuides(false)
		countingDownRef.current = false
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		stopMetronome()
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right
		squatStateSpanRef.current.innerText = stateDisplayTextMap[initState]
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleAddToProgress = ()=>{

		const metrics = []
		const sides = ['left','right']
		sides.forEach( side => {
			metrics.push(...[
				{
					name:`${capitalize(side)} Calf Raise Count`,
					description:'',
					//implicit
					unit_id:1,
					value:repsMaxHeightDifference(
						results[side].reps,
						results[side].centerPosition,
						guidesRef.current[side].startY,
						guidesRef.current[side].height,
						ESTIMATED_HEIGHT_INCHES,
						START_TOLERANCE
					).length
				},{
					name:`${capitalize(side)} Calf Raise Min Height (inches)`,
					description:'',
					//implicit
					unit_id:1,
					value:Math.min(...repsMaxHeightDifference(
						results[side].reps,
						results[side].centerPosition,
						guidesRef.current[side].startY,
						guidesRef.current[side].height,
						ESTIMATED_HEIGHT_INCHES,
						START_TOLERANCE
					)).toFixed(1)
				},{
					name:`${capitalize(side)} Calf Raise Avg Height (inches)`,
					description:'',
					//implicit
					unit_id:1,
					value:arrAvg(repsMaxHeightDifference(
						results[side].reps,
						results[side].centerPosition,
						guidesRef.current[side].startY,
						guidesRef.current[side].height,
						ESTIMATED_HEIGHT_INCHES,
						START_TOLERANCE
					)).toFixed(1)
				},{
					name:`${capitalize(side)} Calf Raise Max Height (inches)`,
					description:'',
					//implicit
					unit_id:1,
					value:Math.max(...repsMaxHeightDifference(
						results[side].reps,
						results[side].centerPosition,
						guidesRef.current[side].startY,
						guidesRef.current[side].height,
						ESTIMATED_HEIGHT_INCHES,
						START_TOLERANCE
					)).toFixed(1)
				}
			])
		})

		dispatch(setMetricCreator({
			show:true,
			category:'AI Single Leg Calf Raise Assessment',
			date:dateToYMD(new Date()),
			aiLog:JSON.stringify(results),
			metrics
		}))
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.2)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'120px',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{
						position:'relative'
					}}>
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							width: "100vw",
							height: "100vh",
						}}>
							
						</svg>
					</div>
					
					{/*<video 
						style={{
							height:`${windowHeight}px`,
							width:`${windowWidth}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/out.mp4"
					/>*/}
					<Webcam 
						style={{
							height: windowWidth >  windowHeight ? '100vh' : 'auto',
							width:  windowWidth >= windowHeight ? 'auto'  : '100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={INPUT_SIZE} height={INPUT_SIZE} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-center mb-2" style={{fontSize:'40px'}}>
							<div>
								<span ref={leftCountSpanRef}>{countRef.current.left}</span>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Left</small>
							</div>
							<div style={{width:'1px',background:'#ccc',margin:'0 16px'}}/>
							<div>
								<span ref={rightCountSpanRef}>{countRef.current.right}</span>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Right</small>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={squatStateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<KeyRepMetricsTable
						leftRepHeights={
							repsMaxHeightDifference(
								results.left.reps,
								results.left.centerPosition,
								guidesRef.current.left.startY,
								guidesRef.current.left.height,
								ESTIMATED_HEIGHT_INCHES,
								START_TOLERANCE
							)
						}
						rightRepHeights={
							repsMaxHeightDifference(
								results.right.reps,
								results.right.centerPosition,
								guidesRef.current.right.startY,
								guidesRef.current.right.height,
								ESTIMATED_HEIGHT_INCHES,
								START_TOLERANCE
							)
						}
					/>
					{/*Object.entries(results).map(([side,data])=>(
						<>
							<div style={{
								fontSize:'24px',
								textAlign:'center',
								margin:'16px 0'
							}}>
								{capitalize(side)} Side
							</div>
							
							<div style={{width:'100%',height:'200px'}}>
								<Line
									options={{
										maintainAspectRatio:false,
										responsive:true,
										animation: {
											duration: 0
										},
										hover: {
											mode: 'nearest'
										},
										scales: {
											xAxes: [{
												ticks:{
													callback:(label)=>(label/1000).toFixed(1),
													maxTicksLimit:10
												},
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Time (seconds)'
												}
											}],//end xAxes
											yAxes: [{
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Height (inches)'
												}
											}]//end yAxes
										}//end scales
									}}
									data={
										{
											labels:data.centerPosition.map((p)=>p.t - data.centerPosition[0].t),
											datasets: [
												{
													lineTension: 0,
													label: 'Height (inches)',
													data:data.centerPosition.map(p=>({
														x:p.t - data.centerPosition[0].t,
														y:roundDecimal((guidesRef.current[side].startY + START_TOLERANCE*guidesRef.current[side].height  - p.y)/guidesRef.current[side].height*69,2),
														status:p.status
													})),
													backgroundColor: "rgba(75,192,192,0.2)",
													borderColor: "rgba(73, 197, 235,1)"
												},{
													showLine:false,
													lineTension: 0,
													label: 'Rep Started',
													fill:false,
													data:data.reps.started.map(t=>({
														x:t - data.centerPosition[0].t,
														y:7
													})),
													borderColor: "rgba(52, 168, 114,1)",
													backgroundColor:'rgba(52, 168, 114,0.2)'
												},{
													showLine:false,
													lineTension: 0,
													label: 'Rep Finished',
													fill:false,
													data:data.reps.ended.map(t=>({
														x:t - data.centerPosition[0].t,
														y:7
													})),
													borderColor: "rgba(230, 137, 16,1)",
													backgroundColor:'rgba(230, 137, 16,0.2)'
												}
											]
										}
									}
								/>
							</div>
						</>
					))*/}
					<Button 
						onClick={handleAddToProgress}
						variant="outline-success" 
						className="my-3"
						block
					>
						Add to {isClient?'':"Client's"} Progress Metrics
					</Button>
					<div className="mt-5 d-flex">
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						{!isWebview()&&
							<Button 
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={()=>{setShowHowTo(false)}}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically score your single leg squat form. 
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Step back until your <strong>full body is in view</strong> of the camera and face the wall.</li>
						<li><span className="mr-2">3.</span>When in position place your hands behind your head to signal the system you are ready to begin.</li>
						<li><span className="mr-2">4.</span>Place hands on wall and lift one foot.</li>
						<li><span className="mr-2">5.</span>Do as many calf raises as you can. Repeat on the other side.</li>
						<li><span className="mr-2">6.</span>View your results</li>
						<li><span className="mr-2">7.</span>If the <span id={styles.RestartHowTo}>Restart</span> button is not in view <strong>scroll down</strong></li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}


export default CalfRaiseDemo